import { FormEvent, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, FormControl, FormControlLabel, FormLabel, IconButton, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import type MeasurementEmbeddedApp from "@nuralogix.ai/web-measurement-embedded-app";
import { loadWmea } from "./sdk";
import { sdkProfile } from "./profile";
import VirtualKeyboard from "../../Components/Modal/Medicao/VirtualKeyboard";
import { FaKeyboard } from "react-icons/fa";
import { DominioURL } from "../../Components/Auth/isAuthenticated";
import ModalComponentInfo from "../../Components/Modal/Info";
import { cancelWmea, getWmeaConfig, saveWmea, startWmea, wmeaError, WmeaProfile, SdkResults } from "../../Services/Wmea";
import PageHeaderUser from "../Auth/Menu";

type Phase = "profile" | "starting" | "measuring" | "saving" | "recovery" | "cancelling" | "complete";
type Pending = { sessionId: string; measurementId?: string; resultSource?: "api" | "sdk_pilot" };
const storageKey = () => `wmea-attempt:${JSON.parse(localStorage.getItem("user") || "{}").id || ""}`;
const readPending = (): Pending | null => {
  try { return JSON.parse(sessionStorage.getItem(storageKey()) || "null"); } catch { return null; }
};

export function MeasurementV2Content() {
  const navigate = useNavigate();
  const container = useRef<HTMLDivElement>(null);
  const app = useRef<MeasurementEmbeddedApp | null>(null);
  const initialization = useRef<Promise<void> | null>(null);
  const mounted = useRef(false);
  const busy = useRef(false);
  const pending = useRef<Pending | null>(readPending());
  const sdkResults = useRef<SdkResults | null>(null);
  const [keyboard, setKeyboard] = useState<"age" | "height" | "weight" | null>(null);
  const aborting = useRef(false);
  const [phase, setPhase] = useState<Phase>(pending.current ? "recovery" : "profile");
  const [config, setConfig] = useState<{ ready: boolean; message?: string; resultSource?: string }>();
  const [error, setError] = useState("");
  const [measurementStatus, setMeasurementStatus] = useState("Preparando câmera e medição…");
  const [stage, setStage] = useState("preparing");
  const [elapsed, setElapsed] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const [profile, setProfile] = useState<Record<keyof WmeaProfile, string>>({ age: "", height: "", weight: "", gender: "", smoking: "", bloodpressuremedication: "", diabetes: "" });

  const remember = (value: Pending | null) => {
    pending.current = value;
    if (value) sessionStorage.setItem(storageKey(), JSON.stringify(value));
    else sessionStorage.removeItem(storageKey());
  };

  const destroy = async () => {
    const current = app.current;
    if (!current) return;
    app.current = null;
    current.on.results = null;
    current.on.error = null;
    current.on.event = null;
    try { await initialization.current; } catch { /* Still release a partially initialized instance. */ }
    try { await current.destroy(); } catch { /* SDK may already have been destroyed. */ }
    // The SDK replaces its host on destroy. Only this imperative subtree belongs to it.
    container.current?.replaceChildren();
  };

  useEffect(() => {
    setElapsed(0);
    if (phase !== "starting" && phase !== "measuring") return;
    const started = Date.now();
    const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [phase, stage]);

  useEffect(() => {
    mounted.current = true;
    let active = true;
    getWmeaConfig().then(value => { if (active) setConfig(value); }).catch(reason => { if (active) setError(wmeaError(reason)); });
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (pending.current) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      active = false;
      mounted.current = false;
      window.removeEventListener("beforeunload", beforeUnload);
      void destroy();
      // Keep only attempt/result IDs for recovery; never persist tokens or indicators.
    };
  }, []);

  const save = async () => {
    const attempt = pending.current;
    if (!attempt?.measurementId || busy.current) return;
    if (attempt.resultSource === "sdk_pilot" && !sdkResults.current) {
      setError("Os indicadores do SDK não estão mais na memória desta página. Não é possível recuperá-los apenas pelo ID neste modo. Encerre a tentativa e faça uma nova captura.");
      return;
    }
    busy.current = true;
    setPhase("saving");
    setError("");
    try {
      const result = attempt.resultSource === "sdk_pilot"
        ? await saveWmea(attempt.sessionId, attempt.measurementId, sdkResults.current!)
        : await saveWmea(attempt.sessionId, attempt.measurementId);
      sdkResults.current = null;
      remember(null);
      await destroy();
      if (mounted.current) navigate(`/medicoes/detalhes/${result.id}`);
    } catch (reason) {
      if (mounted.current) { setError(wmeaError(reason)); setPhase("recovery"); }
    } finally { busy.current = false; }
  };

  const begin = async () => {
    if (busy.current || !config?.ready) return;
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setError("A câmera precisa de HTTPS ou localhost e de um navegador compatível."); return;
    }
    busy.current = true;
    setError("");
    setPhase("starting");
    aborting.current = false;
    setKeyboard(null);
    setStage("preparing");
    setMeasurementStatus("Preparando câmera e medição…");
    const requestId = crypto.randomUUID();
    remember({ sessionId: requestId });
    try {
      const service = JSON.parse(localStorage.getItem("HealthPlaceDados") || "{}");
      const input = { ...profile, age: Number(profile.age), height: Number(profile.height), weight: Number(profile.weight) } as WmeaProfile;
      // Load the SDK only on the new page when starting a measurement.
      // Keep the SDK's native dynamic imports outside CRA/Webpack's module graph.
      const { default: EmbeddedApp, faceAttributeValue } = await loadWmea();
      if (!mounted.current) return;
      const session = await startWmea({ requestId, healthPlace: DominioURL(), customerServiceId: service.id || "", profile: input });
      if (!mounted.current || !container.current) {
        await cancelWmea(session.sessionId); remember(null); return;
      }
      const instance = new EmbeddedApp();
      remember({ sessionId: session.sessionId, resultSource: session.resultSource });
      const sdkHost = document.createElement("div");
      sdkHost.style.height = "100%";
      container.current.replaceChildren(sdkHost);
      app.current = instance;
      instance.on.results = results => {
        if (!mounted.current || aborting.current || pending.current?.measurementId) return;
        if (session.resultSource === "sdk_pilot") {
          sdkResults.current = JSON.parse(JSON.stringify(results));
        }
        remember({ sessionId: session.sessionId, measurementId: results.MeasurementID, resultSource: session.resultSource });
        // Save only after init settles, avoiding lifecycle races in SDK callbacks.
        void Promise.resolve(initialization.current).catch(() => undefined).then(async () => {
          await destroy();
          busy.current = false;
          if (mounted.current) await save();
        });
      };
      instance.on.error = failure => {
        if (!mounted.current || app.current !== instance || aborting.current || pending.current?.measurementId) return;
        const code = String(failure.code);
        const message = code.includes("CAMERA") || code === "NO_DEVICES_FOUND"
          ? "Não foi possível usar a câmera. Confira a permissão e se ela está em uso por outro aplicativo."
          : "Não foi possível concluir a medição. Confira a iluminação, a posição e a conexão e tente novamente.";
        aborting.current = true;
        setPhase("cancelling");
        // Defer until init returns its promise, including errors emitted synchronously by the SDK.
        void Promise.resolve().then(async () => {
          await destroy();
          try {
            await cancelWmea(session.sessionId);
            remember(null);
            sdkResults.current = null;
            if (mounted.current) { setError(message); setPhase("profile"); }
          } catch (reason) {
            if (mounted.current) { setError(`${message} Não foi possível encerrar a tentativa: ${wmeaError(reason)}`); setPhase("recovery"); }
          } finally { busy.current = false; }
        });
      };
      instance.on.event = event => {
        if (!mounted.current || app.current !== instance) return;
        // Never log payloads: they can contain biometric indicators or identifiers.
        switch (event.event) {
          case "CAMERA_STARTED":
            setStage("positioning");
            setMeasurementStatus("Câmera aberta. Posicione o rosto e aguarde as condições de captura; a medição ainda não começou.");
            break;
          case "MEASUREMENT_STARTED":
            setStage("capturing");
            setMeasurementStatus("Medição em andamento. Mantenha o rosto imóvel e bem iluminado.");
            break;
          case "MEASUREMENT_COMPLETED":
            setStage("processing");
            setMeasurementStatus("Captura concluída. Aguardando o resultado do serviço…");
            break;
          case "MEASUREMENT_CANCELED":
            setStage("cancelled");
            setMeasurementStatus("Captura cancelada no aplicativo. Encerre a tentativa pelo botão abaixo.");
            break;
          case "CONSTRAINT_VIOLATION":
            setMeasurementStatus(`Ajuste necessário (${event.payload.code}): centralize o rosto, mantenha-se imóvel e confira a iluminação.`);
            break;
        }
      };
      initialization.current = instance.init({
        container: sdkHost,
        appPath: `${process.env.PUBLIC_URL || ""}/wmea/1.2.0`,
        settings: { token: session.token, refreshToken: session.refreshToken, studyId: session.studyId, measurementOptions: { partnerId: session.sessionId } },
        apiUrl: session.apiHost, profile: sdkProfile(input, faceAttributeValue), language: "pt-BR",
        config: { checkConstraints: true, cancelWhenLowSNR: true, cameraAutoStart: true, measurementAutoStart: true, debugMode: false, downloadPayloads: false },
      });
      await initialization.current;
      if (mounted.current && !aborting.current && !pending.current?.measurementId) setPhase("measuring");
    } catch (reason) {
      if (!aborting.current) {
        await destroy();
        if (mounted.current) { setError(wmeaError(reason)); setPhase("recovery"); }
      }
    } finally { if (!aborting.current) busy.current = false; }
  };

  const cancel = async () => {
    if (busy.current) return;
    busy.current = true;
    setPhase("cancelling");
    setError("");
    try {
      await destroy();
      if (pending.current) await cancelWmea(pending.current.sessionId);
      remember(null);
      sdkResults.current = null;
      if (mounted.current) setPhase("profile");
    } catch (reason) {
      if (mounted.current) { setError(wmeaError(reason)); setPhase("recovery"); }
    } finally { busy.current = false; }
  };

  const submit = (event: FormEvent) => { event.preventDefault(); setInfoOpen(true); };
  const change = (key: keyof WmeaProfile, value: string) => setProfile(previous => ({ ...previous, [key]: value }));
  const cameraVisible = phase === "starting" || phase === "measuring";

  return <Stack spacing={3}>
    <Box><Typography variant="h5" component="h1">Dados pre medição</Typography><Typography color="text.secondary">Sinais Vitais</Typography></Box>
    {error && <Alert severity="error" role="alert">{error}</Alert>}
    {config && !config.ready && <Alert severity="info">{config.message}</Alert>}
    {!config && !error && <Box role="status"><CircularProgress size={22} /> Verificando disponibilidade…</Box>}
    {phase === "profile" && config?.ready && <Box component="form" onSubmit={submit} sx={{ maxWidth: 680 }}>
      <Typography sx={{ mb: 3 }}>Confira seus dados antes de iniciar. A medição será realizada aqui, sem sair do sistema.</Typography>
      <Stack spacing={2}>
        {([['age', 'Idade (anos)', 13, 120, 1], ['height', 'Altura (cm)', 120, 220, 0.1], ['weight', 'Peso (kg)', 30, 300, 0.1]] as const).map(([key, label, min, max, step]) =>
          <Box key={key}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField label={label} type="number" required fullWidth value={profile[key]} onChange={e => change(key, e.target.value)} inputProps={{ min, max, step }} />
              <IconButton type="button" aria-label={`Abrir teclado para ${label}`} onClick={() => setKeyboard(keyboard === key ? null : key)}><FaKeyboard /></IconButton>
            </Stack>
            {keyboard === key && <VirtualKeyboard currentValue={profile[key]} onChange={value => change(key, value)} onClose={() => setKeyboard(null)} />}
          </Box>)}
        {([
          ['gender', 'Sexo atribuído ao nascer', [['male', 'Masculino'], ['female', 'Feminino']]],
          ['smoking', 'Fumante?', [['1', 'Sim'], ['0', 'Não']]],
          ['bloodpressuremedication', 'Utiliza medicamento para pressão?', [['1', 'Sim'], ['0', 'Não']]],
          ['diabetes', 'Diabetes?', [['type1', 'Tipo 1'], ['type2', 'Tipo 2'], ['0', 'Não']]],
        ] as const).map(([key, label, options]) =>
          <FormControl key={key} required>
            <FormLabel id={`${key}-label`}>{label}</FormLabel>
            <RadioGroup row aria-labelledby={`${key}-label`} name={key} value={profile[key]} onChange={e => change(key, e.target.value)}>
              {options.map(([value, text]) => <FormControlLabel key={value} value={value} control={<Radio required />} label={text} />)}
            </RadioGroup>
          </FormControl>)}
        <Button variant="contained" type="submit" size="large">Iniciar medição</Button>
      </Stack>
    </Box>}
    <ModalComponentInfo isOpen={infoOpen} setIsOpen={setInfoOpen} setStartMeasure={() => { void begin(); }} />
    {(phase === "starting" || phase === "saving" || phase === "cancelling") && <Box role="status"><CircularProgress size={22} /> {phase === "starting" ? "Preparando câmera e medição…" : phase === "saving" ? "Confirmando e salvando o resultado…" : "Encerrando tentativa…"}</Box>}
    {cameraVisible && <Alert severity="info" role="status" aria-live="polite">{measurementStatus} Tempo nesta etapa: {elapsed}s.</Alert>}
    {cameraVisible && elapsed >= 90 && <Alert severity="warning">Esta etapa está demorando mais que o esperado. Ainda não há confirmação de resultado. Confira câmera, iluminação e conexão; se não avançar, cancele a tentativa e tente novamente.</Alert>}
    <Box ref={container} sx={{ display: cameraVisible ? "block" : "none", position: "relative", width: "100%", height: "70vh", minHeight: 520 }} />
    {phase === "recovery" && <Alert severity="warning">{pending.current?.measurementId ? "Há um resultado aguardando confirmação. Tentar salvar novamente não realiza outra medição nem duplica o consumo." : "Existe uma tentativa anterior nesta aba. Encerre-a antes de iniciar outra medição."}</Alert>}
    {(phase === "measuring" || phase === "recovery") && <Stack direction="row" spacing={2}>
      {pending.current?.measurementId && <Button variant="contained" onClick={() => { void save(); }}>Tentar salvar resultado</Button>}
      <Button variant="outlined" onClick={() => { void cancel(); }}>{pending.current?.measurementId ? "Descartar tentativa" : "Cancelar tentativa"}</Button>
    </Stack>}
  </Stack>;
}

export default function MeasurementV2Page() {
  return <PageHeaderUser InternalComponent={<MeasurementV2Content />} />;
}
